import { useEffect, useState } from 'react';
import {
  SEASON_SCOPES,
  scopeLabel,
  type SeasonLeaderboardResponse,
  type SeasonScope,
} from '@shared/seasons';
import { ApiError } from '../api';
import { fetchSeasonLeaderboard } from '../apiClient/seasons';
import { useAuth } from '../auth/AuthProvider';
import { useSeason } from '../auth/SeasonProvider';
import { CROWN_GLYPH, CUP_GLYPH, STAR_GLYPH } from '../components/currencyGlyphs';
import { SeasonStatusCard } from './seasons/SeasonStatusCard';
import { SeasonAwardsGallery } from './seasons/SeasonAwardsGallery';
import { SeasonLeaderboardTable } from './seasons/SeasonLeaderboardTable';
import './seasons.css';

/**
 * The Seasons tab: the cross-mode competitive layer. A global trophy case
 * sits up top — the single best award you currently hold in each scope,
 * across the whole season — followed by a scope picker (Overall or a single
 * mode) that drills into your position, the award you'll get / chasing next,
 * and the top-100 board, all ranked by the flat sum of your three currencies.
 *
 * Two data sources: the trophy case reads cross-scope standings from
 * `useSeason()` (`/api/seasons/me`) so it stays put as you switch scope tabs;
 * the per-scope explorer pulls one `/api/seasons/leaderboard` call (entries +
 * the caller's own row + participant count). Head meta is handled by RouteHead
 * (`/seasons` is a noindex utility route), so this page renders no head.
 */
export function SeasonsPage() {
  const { user } = useAuth();
  const { refresh, standings, season } = useSeason();
  const [scope, setScope] = useState<SeasonScope>('overall');
  const [data, setData] = useState<SeasonLeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Landing here is a natural moment to freshen the TopNav crown chip.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSeasonLeaderboard(scope)
      .then((res) => {
        if (!cancelled) {
          setData(res);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        console.error('Failed to load season leaderboard:', err);
        setError(
          err instanceof ApiError
            ? 'Could not load the season board. Try again shortly.'
            : 'Something went wrong loading the season board.',
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scope]);

  const seasonName = data?.season.name ?? 'Seasons';
  const showData = data !== null && data.scope === scope && !loading;

  return (
    <div className="seasons-page" data-testid="seasons-page">
      <header className="seasons-header">
        <p className="seasons-eyebrow">Competitive · Season</p>
        <h1>{seasonName}</h1>
        <p className="seasons-lede">
          Earn your season score on official maps:{' '}
          <span className="seasons-legend-item">
            <span className="seasons-legend-glyph seasons-legend-star">{STAR_GLYPH}</span> stars
          </span>
          ,{' '}
          <span className="seasons-legend-item">
            <span className="seasons-legend-glyph seasons-legend-cup">{CUP_GLYPH}</span> cups
          </span>{' '}
          and{' '}
          <span className="seasons-legend-item">
            <span className="seasons-legend-glyph seasons-legend-crown">{CROWN_GLYPH}</span> crowns
          </span>
          .
        </p>
        <p className="seasons-lede">Community maps keep their own scores and progress, but do not count towards season rankings.</p>
      </header>

      <SeasonAwardsGallery
        standings={standings}
        seasonNumber={season ? season.seasonNumber : null}
      />

      <nav className="seasons-scope-tabs" aria-label="Season scope">
        {SEASON_SCOPES.map((s) => (
          <button
            key={s}
            type="button"
            className={s === scope ? 'seasons-scope-tab is-active' : 'seasons-scope-tab'}
            aria-pressed={s === scope}
            onClick={() => setScope(s)}
          >
            {scopeLabel(s)}
          </button>
        ))}
      </nav>

      {loading && <p className="seasons-status-msg">Loading {scopeLabel(scope)} board…</p>}
      {error && !loading && <p className="seasons-status-msg seasons-error">{error}</p>}

      {showData && data && (
        <div className="seasons-body">
          <SeasonStatusCard scope={scope} me={data.me} participants={data.participants} />
          <SeasonLeaderboardTable
            entries={data.entries}
            me={data.me}
            selfUid={user ? user.uid : null}
          />
        </div>
      )}
    </div>
  );
}
